import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import { ChipGroup } from "./ChipGroup";
import { NameStep } from "./NameStep";
import { SourceTextInput } from "./SourceTextInput";
import { StepNav } from "./StepNav";
import { StepShell } from "./StepShell";
import {
  ROLE_OPTIONS,
  SOURCE_OPTIONS,
  getSourceBranch,
  sourceFollowupHeading,
} from "./options";
import {
  canContinue,
  canGoBack,
  createInitialState,
  isFinalStep,
  patchForStep,
  wizardReducer,
  type StepId,
  type WizardState,
} from "./steps";
import type { AuthUser, OnboardingState, PatchOnboarding } from "./types";

const SAVE_ERROR = "We could not save that. Check your connection and try again.";

export interface OnboardingWizardProps {
  user: AuthUser | null;
  /** Answers already stored server-side, so a half-finished flow resumes. */
  initialOnboarding?: OnboardingState | null;
  patchOnboarding: PatchOnboarding;
  /**
   * Saves the name on the first step, via PATCH /auth/profile. Only a brand-new
   * account (no name yet) sees that step, so a host that only ever hands this
   * wizard already-named users can leave it out.
   */
  updateProfile?: (partial: {
    firstName?: string | null;
    lastName?: string | null;
  }) => Promise<AuthUser>;
  /**
   * Called after the final answer is saved. Optional: a host whose auth store
   * already re-renders on the saved state has nothing extra to do here.
   */
  onComplete?: (onboarding: OnboardingState) => void;
}

/** True once the account has a name to greet, so the name step can be skipped. */
function hasName(user: AuthUser | null): boolean {
  return Boolean(user?.firstName?.trim() || user?.name?.trim());
}

interface StepCopy {
  kicker?: string;
  heading: string;
  subheading?: string;
}

function copyForStep(
  step: StepId,
  /** Set once a source is chosen and its follow-up is showing. */
  sourceBranch: string | null,
): StepCopy {
  switch (step) {
    case "role":
      return { kicker: "Now, about you", heading: "What best describes you?" };
    case "source": {
      // The follow-up asks its own question rather than repeating the first.
      const followup = sourceFollowupHeading(sourceBranch);
      if (followup) {
        return { kicker: "Genuinely curious", heading: followup };
      }
      return {
        kicker: "Genuinely curious",
        heading: "How did you hear about us?",
      };
    }
  }
}

export function OnboardingWizard({
  user,
  initialOnboarding,
  patchOnboarding,
  updateProfile,
  onComplete,
}: OnboardingWizardProps): JSX.Element {
  const [state, dispatch] = useReducer(
    wizardReducer,
    initialOnboarding ?? null,
    createInitialState,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A first-timer has no name yet, so the wizard opens on the name step. Once
  // saved, `nameDone` latches even if the parent has not yet re-rendered with
  // the updated user, so the flow never bounces back to the name question.
  // The most recent onboarding state the server returned, so the final step can
  // complete without having a patch of its own to send.
  const latestOnboarding = useRef<OnboardingState | null>(initialOnboarding ?? null);
  const [nameDone, setNameDone] = useState(false);
  const showNameStep = Boolean(updateProfile) && !hasName(user) && !nameDone;

  const saveName = useCallback(
    async (firstName: string, lastName: string | null) => {
      if (!updateProfile) return;
      await updateProfile({ firstName, lastName });
      setNameDone(true);
    },
    [updateProfile],
  );

  const copy = copyForStep(state.step, state.branch ?? null);
  const ready = canContinue(state);

  const goBack = useCallback(() => {
    setError(null);
    dispatch({ type: "back" });
  }, []);

  const advance = useCallback(async () => {
    if (busy || !canContinue(state)) return;

    const patch = patchForStep(state);
    const final = isFinalStep(state.step);
    setError(null);

    let saved: OnboardingState | null = null;
    if (patch) {
      setBusy(true);
      try {
        saved = await patchOnboarding(patch);
        latestOnboarding.current = saved;
      } catch {
        setError(SAVE_ERROR);
        return;
      } finally {
        setBusy(false);
      }
    }

    if (final) {
      // Finish on the last saved state, falling back only if this step sent no
      // patch. Keying completion off a fresh `saved` alone once left Get
      // Started doing nothing when the final step asked nothing.
      const finished = saved ?? latestOnboarding.current;
      if (finished) onComplete?.(finished);
      return;
    }

    dispatch({ type: "continue" });
  }, [busy, state, patchOnboarding, onComplete]);

  // The wizard is a full-screen surface, so it owns Enter and Escape while it
  // is up. Capture phase plus stopImmediatePropagation keeps the editor's own
  // window-level shortcuts from also firing underneath it.
  useEffect(() => {
    // The name step owns its own inputs and submit, so the wizard's Enter/Escape
    // handling stays out of its way until it is done.
    if (showNameStep) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        goBack();
        return;
      }

      if (event.key !== "Enter") return;

      // Enter on a chip that is not yet chosen belongs to the chip, as a
      // selection. Once that chip is the chosen one, a second Enter means
      // "confirm", which is also what makes click-then-Enter advance.
      const target = event.target as HTMLElement | null;
      const chip = target?.closest?.("[data-onboarding-chip]");
      if (chip && chip.getAttribute("aria-pressed") !== "true") return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void advance();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [advance, goBack, showNameStep]);

  if (showNameStep) {
    return <NameStep user={user} onSave={saveName} />;
  }

  let content: ReactNode = null;

  if (state.step === "role") {
    content = (
      <ChipGroup
        key="role"
        label={copy.heading}
        options={ROLE_OPTIONS}
        value={state.role}
        onSelect={(value) => dispatch({ type: "select", value })}
      />
    );
  } else if (state.step === "source") {
    const branch = getSourceBranch(state.branch);

    if (branch?.kind === "chips") {
      content = (
        <ChipGroup
          key={`source-${state.branch}`}
          label={copy.heading}
          options={branch.options}
          value={state.sourceDetail}
          onSelect={(value) => dispatch({ type: "select", value })}
        />
      );
    } else if (branch?.kind === "text") {
      content = (
        <SourceTextInput
          label={copy.heading}
          placeholder={branch.placeholder}
          value={state.sourceDetail ?? ""}
          onChange={(value) => dispatch({ type: "setSourceDetail", value })}
        />
      );
    } else {
      content = (
        <ChipGroup
          key="source"
          label={copy.heading}
          options={SOURCE_OPTIONS}
          value={state.source}
          onSelect={(value) => dispatch({ type: "select", value })}
        />
      );
    }
  }

  return (
    <StepShell
      kicker={copy.kicker}
      heading={copy.heading}
      subheading={copy.subheading}
      user={user}
      error={error}
      announcement={announcementFor(state, copy)}
      footer={
        <StepNav
          onBack={goBack}
          onContinue={() => void advance()}
          backDisabled={!canGoBack(state)}
          continueDisabled={!ready}
          continueLabel={isFinalStep(state.step) ? "Get Started" : "Continue"}
          busy={busy}
        />
      }
    >
      {content}
    </StepShell>
  );
}

function announcementFor(state: WizardState, copy: StepCopy): string {
  const question = copy.kicker ? `${copy.kicker}. ${copy.heading}` : copy.heading;
  return state.branch ? `${question} Choose an option.` : question;
}
